import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box, Typography, Card, CardContent, TextField, InputAdornment, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Avatar, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Search as SearchIcon, LocalHospital as DoctorIcon, Add as AddIcon,
  Edit as EditIcon, PersonOff as DeactivateIcon,
  Restore as ReactivateIcon,
  DeleteForever as DeleteForeverIcon,
  Send as SendIcon,
  Visibility, VisibilityOff,
} from '@mui/icons-material';

import ClinicTagChipField from '../components/ClinicTagChipField';
import {
  generateUniqueStrongPassword,
  normalizeTags,
  formatTagTitleCase,
} from '../clinicUserFormUtils';
import { PASSWORD_RULES_TEXT, passwordProblem, apiErrorMessage } from '../passwordPolicy';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function statusChip(displayStatus) {
  if (displayStatus === 'email_sent') {
    return <Chip size="small" label="Email sent" color="warning" />;
  }
  if (displayStatus === 'inactive') {
    return <Chip size="small" label="Inactive" />;
  }
  return <Chip size="small" label="Activated" color="success" />;
}

function TagsCell({ tags }) {
  const list = normalizeTags(tags);
  if (!list.length) return '—';
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 280 }}>
      {list.map((t) => (
        <Chip key={t} size="small" label={t} variant="outlined" sx={{ fontWeight: 500 }} />
      ))}
    </Box>
  );
}

export default function ProviderList() {
  const [providers, setProviders] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rowBusy, setRowBusy] = useState(null); // { id, action } while a row action runs
  const [editProvider, setEditProvider] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  /** Prevents browser password autofill until the user focuses the field */
  const [passwordFieldLocked, setPasswordFieldLocked] = useState(true);
  /** One auto-generated password per "Add provider" session; reset when dialog opens or user clears field. */
  const passwordAutoGenDoneRef = useRef(false);
  const [form, setForm] = useState({
    full_name: '',
    email: '',
    password: '',
    speciality: [],
    information: '',
    education: [],
    ehr_practitioner_id: '',
  });

  const loadProviders = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/v1/admin/providers`);
      setProviders(res.data.providers);
      setFiltered(res.data.providers);
    } catch {
      toast.error('Failed to load providers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProviders();
  }, []);

  useEffect(() => {
    if (!searchTerm) {
      setFiltered(providers);
      return;
    }
    const q = searchTerm.toLowerCase();
    setFiltered(
      providers.filter((p) => {
        const spec = normalizeTags(p.speciality).join(' ').toLowerCase();
        const edu = normalizeTags(p.education).join(' ').toLowerCase();
        const ehr = (p.ehr_practitioner_id || '').toLowerCase();
        return (
          (p.full_name || '').toLowerCase().includes(q) ||
          (p.email || '').toLowerCase().includes(q) ||
          spec.includes(q) ||
          edu.includes(q) ||
          ehr.includes(q)
        );
      }),
    );
  }, [searchTerm, providers]);

  const openCreate = () => {
    setEditProvider(null);
    passwordAutoGenDoneRef.current = false;
    setPasswordFieldLocked(true);
    setShowPassword(false);
    setForm({
      full_name: '',
      email: '',
      password: '',
      speciality: [],
      information: '',
      education: [],
      ehr_practitioner_id: '',
    });
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditProvider(p);
    setForm({
      full_name: p.full_name || '',
      email: p.email || '',
      password: '',
      speciality: normalizeTags(p.speciality),
      information: p.information || '',
      education: normalizeTags(p.education),
      ehr_practitioner_id: p.ehr_practitioner_id || '',
    });
    setDialogOpen(true);
  };

  const handlePasswordFocus = useCallback(() => {
    if (passwordAutoGenDoneRef.current) return;
    setForm((prev) => {
      if (prev.password) return prev;
      if (passwordAutoGenDoneRef.current) return prev;
      passwordAutoGenDoneRef.current = true;
      return { ...prev, password: generateUniqueStrongPassword() };
    });
  }, []);

  const handleGeneratePasswordClick = useCallback(() => {
    setPasswordFieldLocked(false);
    passwordAutoGenDoneRef.current = true;
    setForm((prev) => ({ ...prev, password: generateUniqueStrongPassword() }));
  }, []);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (editProvider) {
        const body = {};
        if (form.full_name) body.full_name = form.full_name;
        if (form.email && form.email !== editProvider.email) body.email = form.email;
        body.speciality = form.speciality;
        if (form.information !== undefined) body.information = form.information;
        body.education = form.education;
        body.ehr_practitioner_id = form.ehr_practitioner_id?.trim() || null;
        await axios.put(`${API_URL}/api/v1/admin/providers/${editProvider.id}`, body);
        toast.success('Provider updated');
      } else {
        if (!form.password) {
          toast.error('Password is required');
          return;
        }
        const problem = passwordProblem(form.password);
        if (problem) {
          toast.error(problem);
          return;
        }
        if (!form.speciality.length) {
          toast.error('Add at least one speciality');
          return;
        }
        const res = await axios.post(`${API_URL}/api/v1/admin/providers`, {
          full_name: form.full_name,
          email: form.email,
          password: form.password,
          speciality: form.speciality,
          information: form.information?.trim() || null,
          education: form.education,
          ehr_practitioner_id: form.ehr_practitioner_id?.trim() || null,
        });
        toast.success(res.data?.message || 'Provider added');
      }
      setDialogOpen(false);
      loadProviders();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (p) => {
    if (rowBusy || !window.confirm(`Deactivate ${p.full_name}? They will not be able to sign in until reactivated.`)) return;
    setRowBusy({ id: p.id, action: 'deactivate' });
    try {
      await axios.delete(`${API_URL}/api/v1/admin/providers/${p.id}`);
      toast.success('Provider deactivated');
      loadProviders();
    } catch {
      toast.error('Failed to deactivate');
    } finally {
      setRowBusy(null);
    }
  };

  const handleReactivate = async (p) => {
    if (rowBusy || !window.confirm(`Reactivate ${p.full_name}? They will be able to sign in again.`)) return;
    setRowBusy({ id: p.id, action: 'reactivate' });
    try {
      await axios.put(`${API_URL}/api/v1/admin/providers/${p.id}`, { is_active: true });
      toast.success('Provider reactivated');
      loadProviders();
    } catch {
      toast.error('Failed to reactivate');
    } finally {
      setRowBusy(null);
    }
  };

  const handlePermanentDelete = async (p) => {
    if (rowBusy || !window.confirm(
        `Permanently delete ${p.full_name} (${p.email})?\n\nThis removes their account, sessions, and notes they created. Patient assignments to this provider will be cleared. This cannot be undone.`,
      )) {
      return;
    }
    setRowBusy({ id: p.id, action: 'delete' });
    try {
      await axios.delete(`${API_URL}/api/v1/admin/providers/${p.id}/permanent`);
      toast.success('Provider permanently deleted');
      loadProviders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete provider');
    } finally {
      setRowBusy(null);
    }
  };

  const handleResendWelcome = async (p) => {
    if (rowBusy || !window.confirm(
      `Resend the welcome email to ${p.full_name || p.email}?\n\n` +
      `This generates a NEW password and a fresh activation link — their current password will stop working.`
    )) return;
    setRowBusy({ id: p.id, action: 'resend' });
    try {
      const res = await axios.post(`${API_URL}/api/v1/admin/providers/${p.id}/resend-welcome`);
      toast.success(res.data?.message || 'Welcome email re-sent');
      loadProviders();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to resend welcome email');
    } finally {
      setRowBusy(null);
    }
  };

  const createDisabled =
    !form.full_name?.trim() ||
    !form.email?.trim() ||
    !form.password ||
    form.speciality.length === 0;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: 'secondary.main', width: 48, height: 48 }}><DoctorIcon /></Avatar>
          <Box>
            <Typography variant="h4" fontWeight={700}>Providers</Typography>
            <Typography variant="body2" color="text.secondary">Doctors and clinical staff in your clinic</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Provider</Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            fullWidth
            placeholder="Search by name, email, EHR ID, speciality, or education…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>
              ),
            }}
          />
        </CardContent>
      </Card>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Speciality</TableCell>
              <TableCell>Email</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5}>Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={5}>No providers found.</TableCell></TableRow>
            ) : (
              filtered.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell><Typography fontWeight={600}>{p.full_name || '—'}</Typography></TableCell>
                  <TableCell><TagsCell tags={p.speciality} /></TableCell>
                  <TableCell>{p.email}</TableCell>
                  <TableCell align="center">{statusChip(p.display_status)}</TableCell>
                  <TableCell align="center">
                    <Tooltip title={p.provider_onboarding_status === 'activated'
                      ? 'Already activated — this provider has accepted their invite, so no welcome email is needed'
                      : 'Resend welcome email (generates a new password)'}>
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={p.provider_onboarding_status === 'activated' || rowBusy?.id === p.id}
                          onClick={() => handleResendWelcome(p)}
                        >
                          {rowBusy?.id === p.id && rowBusy?.action === 'resend' ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <span>
                        <IconButton size="small" disabled={rowBusy?.id === p.id} onClick={() => openEdit(p)}><EditIcon /></IconButton>
                      </span>
                    </Tooltip>
                    {p.is_active ? (
                      <Tooltip title="Deactivate (can reactivate later)">
                        <span>
                          <IconButton size="small" color="warning" disabled={rowBusy?.id === p.id} onClick={() => handleDeactivate(p)}>
                            {rowBusy?.id === p.id && rowBusy?.action === 'deactivate' ? <CircularProgress size={18} color="inherit" /> : <DeactivateIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Reactivate">
                        <span>
                          <IconButton size="small" color="success" disabled={rowBusy?.id === p.id} onClick={() => handleReactivate(p)}>
                            {rowBusy?.id === p.id && rowBusy?.action === 'reactivate' ? <CircularProgress size={18} color="inherit" /> : <ReactivateIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete permanently">
                      <span>
                        <IconButton size="small" color="error" disabled={rowBusy?.id === p.id} onClick={() => handlePermanentDelete(p)}>
                          {rowBusy?.id === p.id && rowBusy?.action === 'delete' ? <CircularProgress size={18} color="inherit" /> : <DeleteForeverIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => { if (!saving) setDialogOpen(false); }} maxWidth="md" fullWidth>
        <DialogTitle>{editProvider ? 'Edit Provider' : 'Add New Provider'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Full Name"
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            margin="normal"
            required
          />
          <ClinicTagChipField
            label="Speciality"
            required={!editProvider}
            value={form.speciality}
            onChange={(tags) => setForm({ ...form, speciality: tags })}
            helperText="Add one or more specialties. Press Enter or comma after each. Click × on a chip to remove. Each word is capitalized automatically."
            placeholder="e.g. Cardiology"
            guardAutofill
            inputName="provider-speciality-tags"
            formatTag={formatTagTitleCase}
          />
          <TextField
            fullWidth
            label="Information"
            value={form.information}
            onChange={(e) => setForm({ ...form, information: e.target.value })}
            margin="normal"
            multiline
            minRows={2}
            placeholder="Short introduction shown internally"
          />
          <TextField
            fullWidth
            label="EHR Identifier Number"
            value={form.ehr_practitioner_id}
            onChange={(e) => setForm({ ...form, ehr_practitioner_id: e.target.value })}
            margin="normal"
            placeholder="Optional — ID from your EHR system"
            helperText="Optional. Add only if you map providers to your EHR; leave blank otherwise."
            inputProps={{ autoComplete: 'off', name: 'provider-ehr-id' }}
          />
          <ClinicTagChipField
            label="Education"
            required={false}
            value={form.education}
            onChange={(tags) => setForm({ ...form, education: tags })}
            helperText="Degrees and credentials as separate tags. Press Enter after each. Each word is capitalized automatically."
            placeholder="e.g. MD"
            guardAutofill
            inputName="provider-education-tags"
            formatTag={formatTagTitleCase}
          />
          {!editProvider && (
            <Box>
              <TextField
                fullWidth
                label="Password"
                name="new-provider-password"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={(e) => {
                  const v = e.target.value;
                  if (v === '') {
                    passwordAutoGenDoneRef.current = false;
                  }
                  setForm({ ...form, password: v });
                }}
                onFocus={() => {
                  if (passwordFieldLocked) setPasswordFieldLocked(false);
                  handlePasswordFocus();
                }}
                margin="normal"
                required
                helperText={`Focus the field or use the button below to generate a strong password, or type your own. Use the eye icon to show or hide. ${PASSWORD_RULES_TEXT} They'll be asked to choose their own when they first sign in.`}
                inputProps={{
                  autoComplete: 'new-password',
                  readOnly: passwordFieldLocked,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={showPassword ? 'Hide password' : 'Show password'}>
                        <IconButton
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => setShowPassword((s) => !s)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={handleGeneratePasswordClick}
                sx={{ mt: 0.5, mb: 1 }}
              >
                Generate strong password
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              saving || (
                editProvider
                  ? !form.full_name?.trim() || !form.email?.trim() || form.speciality.length === 0
                  : createDisabled
              )
            }
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? (editProvider ? 'Saving…' : 'Creating…') : (editProvider ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
